/* eslint-disable react/prop-types */
import {
  useState, useContext, useEffect, useRef,
  useCallback, useMemo, memo
} from 'react';
import PlusIcon from './icons/plus.svg?react'
import MagnifyingGlassIcon from './icons/magnifyingglass.svg?react'
import ClockIcon from './icons/clock.svg?react'
import StarIcon from './icons/star.svg?react'
import CommandIcon from './icons/command.svg?react';

import { AppContext } from './AppContext.tsx';
import { useNavigate, useLocation } from 'react-router-dom';
import HistoryEntry from './HistoryEntry.jsx';
import Authentication from './Authentication.jsx';
import SidenavIcon from './icons/sidebarleft.svg?react'
import useVerticalRovingFocus from '../utils/useVerticalRovingFocus.jsx';
import { SkeletonConversation } from './Skeleton.jsx';
import Switch from './Switch.jsx';
import FeedbackElement from './FeedbackElement.jsx';
import { NavButton } from './NavButton.jsx';
import { IconButton } from './primitives/IconButton.jsx';
import { getPlatformModifiers } from '../hooks/keybinding/utils.js';
import { isElectron } from '../utils/platform.js';
import { useFullscreen } from '../hooks/useFullscreen.js';

/* ------------------------------------------------------------------ */
/*  1. Helpers — still in this file                                   */
/* ------------------------------------------------------------------ */
function useMenuAnimation(isOpen, delay = 300) {
  const [ready, set] = useState(false);
  useEffect(() => {
    if (!isOpen) return set(false);
    const id = setTimeout(() => set(true), delay);
    return () => clearTimeout(id);
  }, [isOpen, delay]);
  return ready;
}

/* ------------------------------------------------------------------ */
/*  3. Main component                                                  */
/* ------------------------------------------------------------------ */
function Menu() {
  const {
    menuIsOpen,
    onMenuIsOpenChange,
    favoriteConversations,
    conversations,
    conversationsLoading,
    selectedConversation,
    authSession,
    startNewConversation,
    setSearchModal
  } = useContext(AppContext);
  // React Router navigation
  const navigate = useNavigate();
  const location = useLocation();

  const menuRef = useRef(null);
  const menuContainerRef = useRef(null);
  const feedbackRef = useRef(null); // Holds both popover and modal refs

  // State for recent and favorite conversations open/closed
  const [recentConversationsOpen, setRecentConversationsOpen] = useState(false);
  const [favoriteConversationsOpen, setFavoriteConversationsOpen] = useState(false);

  // Keybinding modifier based on OS
  const { primary, primarySymbol } = getPlatformModifiers();
  const isMac = primary === 'Meta';
  const isFullScreen = useFullscreen();

  /* ---------- hooks ---------- */
  const menuReady = useMenuAnimation(menuIsOpen, 300);

  // UPDATED: Check menu, popover, AND modal refs
  useEffect(() => {
    if (!menuIsOpen) return;
    const handler = e => {
      // ignore if large screen
      if (window.innerWidth >= 1024) return;

      // Check if click is outside menu AND outside both feedback elements
      const isOutsideMenu = menuRef.current && !menuRef.current.contains(e.target);
      const isOutsidePopover = !feedbackRef.current?.popoverRef || !feedbackRef.current.popoverRef.contains(e.target);
      const isOutsideModal = !feedbackRef.current?.modalRef || !feedbackRef.current.modalRef.contains(e.target);

      if (isOutsideMenu && isOutsidePopover && isOutsideModal) {
        onMenuIsOpenChange(false);
      }
    };
    document.addEventListener('pointerdown', handler);
    return () => document.removeEventListener('pointerdown', handler);
  }, [menuIsOpen, onMenuIsOpenChange]);

  const RECENT_LIMIT = 5;

  const { onKeyDown } = useVerticalRovingFocus(
    menuContainerRef,
    menuReady,
    [favoriteConversations.length + Math.min(conversations.length, RECENT_LIMIT), menuIsOpen]
  );

  /* ---------- callbacks ---------- */
  const closeMenu = useCallback(() => onMenuIsOpenChange(p => !p), [onMenuIsOpenChange]);
  const newChat = useCallback(() => {
    // Clear state then navigate to start a new chat
    startNewConversation()
    navigate('/chat')
    if (menuIsOpen) closeMenu()
  }, [startNewConversation, navigate, menuIsOpen, closeMenu])
  const toggleSearch = useCallback(() => setSearchModal(p => !p), [setSearchModal]);

  /* ---------- memo ---------- */
  const getUpdatedMs = useCallback((c) => {
    if (Number.isFinite(c?.updatedAtMs)) return c.updatedAtMs;
    const updatedParsed = c?.updatedAt ? Date.parse(c.updatedAt) : NaN;
    if (Number.isFinite(updatedParsed)) return updatedParsed;

    if (Number.isFinite(c?.createdAtMs)) return c.createdAtMs;
    const createdParsed = c?.createdAt ? Date.parse(c.createdAt) : NaN;
    return Number.isFinite(createdParsed) ? createdParsed : 0;
  }, []);

  const recentList = useMemo(() => {
    if (!menuIsOpen) return [];

    return [...conversations]
      .sort((a, b) => getUpdatedMs(b) - getUpdatedMs(a))
      .slice(0, RECENT_LIMIT)
      .map(x => (
        <HistoryEntry
          data-nav-item
          key={x.conversationId}
          title={x.title || "New Conversation"}
          conversationId={x.conversationId}
          isFavorite={x.favorite}
          updatedAt={x.updatedAt}
          isSelected={selectedConversation?.conversationId === x.conversationId}
          onSelect={() => {
            navigate(`/chat/${x.conversationId}`)
            if (menuIsOpen) closeMenu()
          }}
        />
      ));
  }, [conversations, selectedConversation?.conversationId, navigate, menuIsOpen, closeMenu, getUpdatedMs]);

  const favoriteList = useMemo(() => {
    return [...favoriteConversations]
      .sort((a, b) => getUpdatedMs(b) - getUpdatedMs(a))
      .map(x => (
        <HistoryEntry
          data-nav-item
          key={x.conversationId}
          title={x.title || "New Conversation"}
          conversationId={x.conversationId}
          isFavorite={x.favorite}
          updatedAt={x.updatedAt}
          isSelected={selectedConversation?.conversationId === x.conversationId}
          isNested={true}
          isTabbable={favoriteConversationsOpen}
          onSelect={() => {
            navigate(`/chat/${x.conversationId}`)
            if (menuIsOpen) closeMenu()
          }}
        />
      ));
  }, [favoriteConversations, selectedConversation?.conversationId, navigate, menuIsOpen, closeMenu, getUpdatedMs, favoriteConversationsOpen]);

  /* ---------- render ---------- */
  return (
    <>
      <nav
        ref={menuRef}
        aria-label="Main navigation"
        className={`fixed left-0 top-0 flex flex-col gap-1 justify-between h-screen-mobile
        text-primary z-30 transition-all duration-300 ease-in-out 
        overflow-y-visible bg-bg-panel md:bg-bg-page px-spacing-2 py-spacing-2 
        ${isElectron() && !isFullScreen ? 'pt-[38px]' : ''} 
        ${menuIsOpen
            ? 'w-[244px]'
            : 'w-[48px]'
          } ${menuIsOpen ? 'translate-x-0' : '-translate-x-full md:-translate-x-0'}`
        }
      >
        <div
          ref={menuContainerRef}
          role="menu"
          aria-orientation="vertical"
          onKeyDown={onKeyDown}
          className="flex flex-col h-full overflow-hidden"
        >
          {/* Header */}
          <div className="flex flex-col mb-spacing-6">
            <div className="w-fit mb-spacing-1">
              <IconButton
                variant="ghost"
                rounded="sm"
                focusStyle="subtle"
                onClick={closeMenu}
                aria-label="Close navigation menu"
                aria-expanded={menuIsOpen}
              >
                <SidenavIcon />
              </IconButton>
            </div>
            {authSession && (
              <div className="flex flex-col gap-spacing-1" role="group" aria-label="Primary actions">
                <NavButton
                  menuIsOpen={menuIsOpen}
                  icon={<PlusIcon />}
                  label="New chat"
                  onClick={newChat}
                  aria-label="Start a new chat"
                />
                <NavButton
                  menuIsOpen={menuIsOpen}
                  icon={<MagnifyingGlassIcon />}
                  label="Search conversations"
                  onClick={toggleSearch}
                  aria-label="Search conversations"
                />
                {/*
                <NavButton
                  icon={<ClockIcon className="size-4 shrink-0" aria-hidden="true" />}
                  label="Recent conversations"
                  onClick={() => setRecentConversationsOpen(p => !p)}
                  aria-label="Recent conversations"
                />
                {recentConversationsOpen && (
                  <>
                    {(conversationsLoading) ? (
                      <div className="flex flex-col py-spacing-1 gap-spacing-1">
                        {[...Array(3)].map((_, i) => (
                          <SkeletonConversation key={i} />
                        ))}
                      </div>
                    ) : (recentList.length === 0) ? (
                      <div className="p-spacing-4 border border-dashed border-border-default rounded-lg text-sm flex items-center justify-center text-secondary">
                        Your most recent conversations will show up here
                      </div>
                    ) : (
                      <div className="flex flex-col py-spacing-1 gap-spacing-1">{recentList}</div>
                    )}
                  </>
                )}
                */}
                {menuIsOpen && (
                  <>
                    <NavButton
                      menuIsOpen={menuIsOpen}
                      label="Favorite conversations"
                      onClick={() => setFavoriteConversationsOpen(p => !p)}
                      aria-label="Favorite conversations"
                      active={favoriteConversationsOpen}
                      showChevron={true}
                    />
                    {favoriteConversationsOpen && (
                      <>
                        {conversationsLoading ? (
                          <div className="flex flex-col py-spacing-1 gap-spacing-1">
                            {[...Array(3)].map((_, i) => (
                              <SkeletonConversation key={i} />
                            ))}
                          </div>
                        ) : (favoriteList.length === 0) ? (
                          <div className="p-spacing-4 border border-dashed border-border-default rounded-lg text-sm flex items-center justify-center text-secondary">
                            <span>
                              Press{" "}
                              <kbd className="border border-border-default text-primary bg-bg-well inline-flex items-center justify-center text-dls-body leading-none p-spacing-1 h-[24px] w-[24px] rounded min-w-[20px] align-middle">
                                t
                              </kbd>{" "}
                              +{" "}
                              <kbd className="border border-border-default text-primary bg-bg-well inline-flex items-center justify-center text-dls-body leading-none p-spacing-1 h-[24px] w-[24px] rounded min-w-[20px] align-middle">
                                f
                              </kbd>{" "}
                              to favorite the current conversation
                            </span>
                          </div>
                        ) : (
                          <div className="flex flex-col py-spacing-1 gap-spacing-1">{favoriteList}</div>
                        )}
                      </>
                    )}
                  </>
                )}
              </div>
            )}

          </div>

          {/* Auth */}
          <div className="mt-auto">
            <FeedbackElement ref={feedbackRef} />
            <Authentication />
          </div>
        </div>
      </nav>
    </>
  );
}

export default memo(Menu);
